import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { FlexColumn, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { CurrentEnrollments } from './current-enrollments';
import { messages } from './messages';
import style from './security.module.less';
import { StepUpAuthContextProvider } from '../../../../../global/step-up-authentication';
import { trackPasswordChallenge, trackSuccess, USER_ACTIONS } from '../../../../../global/step-up-authentication/analytics-helpers';
import { useModal } from '../../../../../hooks/use-modal';

const Phases = {
  IDLE: 'IDLE',
  CHALLENGING: 'CHALLENGING',
  AUTHENTICATED: 'AUTHENTICATED',
};

const featureName = USER_ACTIONS.MFA_SETTINGS;

export const AuthGatedSection = () => {
  const { isOpen, open, close } = useModal();
  const [phase, setPhase] = useAsyncSafeState(Phases.IDLE);
  const [hasTimeoutError, setHasTimeoutError] = useAsyncSafeState(false);

  const showTimeoutError = useCallback(() => {
    setPhase(Phases.IDLE);
    setHasTimeoutError(true);
    close();
  }, [setHasTimeoutError, setPhase, close]);

  const hideTimeoutError = useCallback(() => setHasTimeoutError(false), [setHasTimeoutError]);

  const startStepUpAuth = useCallback(() => {
    setPhase(Phases.CHALLENGING);
    open();
    trackPasswordChallenge({ source: featureName });
  }, [setPhase, open]);

  const onCloseModal = useCallback(() => {
    close();
    setPhase(Phases.IDLE);
  }, [close, setPhase]);

  const onStepUpAuthSuccess = useCallback(() => {
    setPhase(Phases.AUTHENTICATED);
    trackSuccess({ source: featureName });
  }, [setPhase]);

  return (
    <FlexColumn className={style['mfa-section']} gap={Gap.Space16}>
      {phase !== Phases.AUTHENTICATED && (
        <>
          <Button className={style['manage-mfa-button']} onClick={startStepUpAuth} theme={THEME_CONSTANTS.secondary}>
            <FormattedMessage {...messages.manageSettingsButton} />
          </Button>
          {hasTimeoutError ? (
            <BannerMessageV2
              content={<Text textType={TextType.Paragraph} value={messages.timeoutErrorMessage} />}
              onClose={hideTimeoutError}
              status={BannerStatus.Error}
            />
          ) : null}
        </>
      )}
      {phase !== Phases.IDLE && (
        <StepUpAuthContextProvider
          isMfaSettings
          isModalOpen={isOpen}
          onClose={onCloseModal}
          onStepUpAuthSuccess={onStepUpAuthSuccess}
          onTokenTimeout={showTimeoutError}
          source={featureName}
          stepUpAuthModalTitle={messages.stepUpAuthModalTitle}
        >
          <CurrentEnrollments />
        </StepUpAuthContextProvider>
      )}
    </FlexColumn>
  );
};
